import { transformEnumToDisplay } from '@fruitful/member-profile';
import { useAtomValue } from 'jotai';
import { Column, type ColumnEditorOptions } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
} from 'primereact/datatable';
import { useState } from 'react';
import { H6Emphasized, Spinner, useToastController, YStack } from 'smoothie';

import {
    DropdownEditor,
    TextEditor,
} from '../../common/components/GridCellEditors';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { US_STATES } from '../../common/constants/States';
import {
    adminUserAtom,
    selectedMembershipAtom,
} from '../../entities/entityAtoms';
import {
    type MemberDetails,
    type UserAddress,
} from '../../entities/entityTypes';
import { useUpsertAddressAndRefetch } from '../hooks/useUpsertAddress';
interface AddressInfoProps {
    userAddresses: Array<UserAddress | null>;
}

export const AddressTable = ({ userAddresses }: AddressInfoProps) => {
    const toast = useToastController();

    const adminUser = useAtomValue(adminUserAtom);
    const canEditAddress =
        adminUser.authorization.selectedMembership.addresses.canEdit;

    const membership = useAtomValue(selectedMembershipAtom);
    const users = membership?.users ?? [];

    const [isSubmitting, setIsSubmitting] = useState(false);
    const data = userAddresses.filter((a) => a != null);

    const { upsertAddressAsync } = useUpsertAddressAndRefetch(membership);

    const getFormattedAddressOwner = (userId: string) => {
        const owner = users.find((user) => user.id === userId);
        return owner?.firstName ?? '(Unknown)';
    };

    const onRowEditComplete = async ({
        newData,
    }: DataTableRowEditCompleteEvent) => {
        setIsSubmitting(true);
        const address: UserAddress = newData as UserAddress;

        try {
            await upsertAddressAsync({
                id: address.id,
                userId: address.userId,
                type: address.type,
                line1: address.line1,
                ...(address.line2 != null ? { line2: address.line2 } : {}),
                city: address.city,
                postalCode: address.postalCode,
                ...(address.state != null ? { state: address.state } : {}),
                countryCode: 'US',
            });

            toast.show('Address updated!', MutationSuccessToastOptions);
        } catch (error) {
            toast.show('Error updating address', MutationErrorToastOptions());
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <YStack gap={16}>
            <H6Emphasized paddingTop={40}>Address details</H6Emphasized>
            {isSubmitting ? (
                <Spinner />
            ) : (
                <DataTable
                    value={data}
                    size="small"
                    tableStyle={{ width: '100%', fontSize: 13 }}
                    editMode="row"
                    onRowEditComplete={onRowEditComplete}
                    scrollable
                    disabled={isSubmitting}
                >
                    <Column field="id" header="id" hidden={true} />
                    <Column
                        field="type"
                        header="type"
                        body={({ type }: UserAddress) =>
                            transformEnumToDisplay(type)
                        }
                    />
                    <Column
                        field="userId"
                        header="Belongs To"
                        body={({ userId }: UserAddress) =>
                            getFormattedAddressOwner(userId)
                        }
                        editor={(options) =>
                            BelongsToUserEditor(options, users)
                        }
                    />
                    <Column
                        field="line1"
                        header="Address Line 1"
                        style={{
                            minWidth: 200,
                        }}
                        editor={(options) => TextEditor({ options })}
                    />
                    <Column
                        field="line2"
                        header="Address Line 2"
                        style={{
                            minWidth: 200,
                        }}
                        editor={(options) => TextEditor({ options })}
                    />
                    <Column
                        field="city"
                        header="City"
                        style={{
                            minWidth: 100,
                        }}
                        editor={(options) => TextEditor({ options })}
                    />
                    <Column
                        field="state"
                        header="State"
                        editor={(options) => StateEditor(options)}
                    />
                    <Column
                        field="postalCode"
                        header="Postal Code"
                        style={{
                            minWidth: 100,
                        }}
                        editor={(options) => TextEditor({ options })}
                    />
                    <Column field="countryCode" header="Country" />
                    <Column rowEditor hidden={!canEditAddress} />
                </DataTable>
            )}
        </YStack>
    );
};

const BelongsToUserEditor = (
    options: ColumnEditorOptions,
    users: MemberDetails[],
) => {
    return (
        <DropdownEditor
            options={options}
            dropdownProps={{
                options: users,
                optionLabel: 'firstName',
                optionValue: 'id',
                placeholder: 'Belongs to',
            }}
        />
    );
};

const StateEditor = (options: ColumnEditorOptions) => {
    return (
        <DropdownEditor
            options={options}
            dropdownProps={{
                options: US_STATES,
                optionLabel: 'value',
                optionValue: 'value',
                placeholder: 'State',
            }}
        />
    );
};
